<template>
  <div>
    <Head title="Configurazione" />
    <h1 class="mb-8 text-3xl font-bold">Configurazione Sistema</h1>

    <!-- Global Emergency Closure -->
    <div class="mb-8 p-6 bg-white rounded-lg shadow">
      <h2 class="text-xl font-bold mb-4">Chiusura di Emergenza Globale</h2>
      <div class="flex items-center">
        <label class="flex items-center cursor-pointer">
          <input
            type="checkbox"
            v-model="localForceClosedGlobal"
            @change="toggleGlobalClosure"
            class="mr-2 w-4 h-4"
          />
          <span class="text-lg">Forza chiusura per tutte le stagioni</span>
        </label>
      </div>
      <p class="text-sm text-gray-600 mt-2">
        Questa opzione sovrascrive tutte le configurazioni di stagione e chiude immediatamente la piscina.
      </p>
    </div>

    <!-- Seasons List -->
    <div class="mb-8 p-6 bg-white rounded-lg shadow">
      <div class="flex justify-between items-center mb-4">
        <h2 class="text-xl font-bold">Stagioni Configurate</h2>
        <button
          @click="openSeasonModal(null)"
          class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          Aggiungi Stagione
        </button>
      </div>

      <div v-if="seasons.length === 0" class="text-gray-500 text-center py-8">
        Nessuna stagione configurata. Aggiungi la prima stagione per iniziare.
      </div>

      <div v-for="season in sortedSeasons" :key="season.id" class="border rounded-lg p-4 mb-4">
        <div class="flex justify-between items-start">
          <div class="flex-1">
            <h3 class="text-lg font-semibold">{{ season.name }}</h3>
            <p class="text-sm text-gray-600">
              Dal {{ formatDate(season.start_date) }} al {{ formatDate(season.end_date) }}
            </p>
            <div class="mt-2 flex gap-4">
              <span class="text-sm">
                <strong>Adulti:</strong> €{{ season.pricing.adults }}
              </span>
              <span class="text-sm">
                <strong>Bambini:</strong> €{{ season.pricing.kids }}
              </span>
              <span class="text-sm">
                <strong>Ombrellone:</strong> €{{ season.pricing.umbrella }}
              </span>
              <span class="text-sm">
                <strong>Postazione:</strong> €{{ season.pricing.station }}
              </span>
              <span class="text-sm">
                <strong>Lettino Extra:</strong> €{{ season.pricing.extra_bed }}
              </span>
            </div>
            <div v-if="season.force_closed" class="mt-2">
              <span class="bg-red-500 text-white px-2 py-1 rounded text-xs">
                CHIUSO MANUALMENTE
              </span>
            </div>
            <div v-if="isActiveSeason(season)" class="mt-2">
              <span class="bg-green-500 text-white px-2 py-1 rounded text-xs">
                STAGIONE ATTIVA
              </span>
            </div>
          </div>
          <div class="flex gap-2">
            <button
              @click="openSeasonModal(season)"
              class="bg-yellow-500 hover:bg-yellow-700 text-white font-bold py-1 px-3 rounded"
            >
              Modifica
            </button>
            <button
              @click="deleteSeason(season.id)"
              class="bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-3 rounded"
            >
              Elimina
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Season Modal -->
    <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" @click.self="closeModal">
      <div class="bg-white rounded-lg p-8 max-w-2xl w-full max-h-screen overflow-y-auto">
        <h2 class="text-2xl font-bold mb-4">
          {{ editingSeason ? 'Modifica Stagione' : 'Nuova Stagione' }}
        </h2>

        <form @submit.prevent="saveSeason">
          <div class="mb-4">
            <label class="block text-gray-700 text-sm font-bold mb-2">Nome Stagione</label>
            <input
              v-model="seasonForm.name"
              type="text"
              required
              class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              placeholder="es. Estate 2025"
            />
          </div>

          <div class="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label class="block text-gray-700 text-sm font-bold mb-2">Data Inizio</label>
              <input
                v-model="seasonForm.start_date"
                type="date"
                required
                class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              />
            </div>
            <div>
              <label class="block text-gray-700 text-sm font-bold mb-2">Data Fine</label>
              <input
                v-model="seasonForm.end_date"
                type="date"
                required
                class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              />
            </div>
          </div>

          <div class="mb-4">
            <label class="flex items-center cursor-pointer">
              <input
                type="checkbox"
                v-model="seasonForm.force_closed"
                class="mr-2 w-4 h-4"
              />
              <span>Forza chiusura per questa stagione</span>
            </label>
          </div>

          <h3 class="text-lg font-bold mb-2">Prezzi (€)</h3>

          <div class="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label class="block text-gray-700 text-sm font-bold mb-2">Adulti</label>
              <input
                v-model.number="seasonForm.pricing.adults"
                type="number"
                required
                min="0"
                class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              />
            </div>
            <div>
              <label class="block text-gray-700 text-sm font-bold mb-2">Bambini</label>
              <input
                v-model.number="seasonForm.pricing.kids"
                type="number"
                required
                min="0"
                class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              />
            </div>
            <div>
              <label class="block text-gray-700 text-sm font-bold mb-2">Ombrellone</label>
              <input
                v-model.number="seasonForm.pricing.umbrella"
                type="number"
                required
                min="0"
                class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              />
            </div>
            <div>
              <label class="block text-gray-700 text-sm font-bold mb-2">Postazione</label>
              <input
                v-model.number="seasonForm.pricing.station"
                type="number"
                required
                min="0"
                class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              />
            </div>
            <div>
              <label class="block text-gray-700 text-sm font-bold mb-2">Lettino Extra</label>
              <input
                v-model.number="seasonForm.pricing.extra_bed"
                type="number"
                required
                min="0"
                class="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              />
            </div>
          </div>

          <div class="flex justify-end gap-2">
            <button
              type="button"
              @click="closeModal"
              class="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
            >
              Annulla
            </button>
            <button
              type="submit"
              class="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
            >
              Salva
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import DashboardLayout from "@/Pages/Dashboard/Shared/DashboardLayout";
import { Head, router } from "@inertiajs/vue3";
import moment from "moment";

export default {
  components: { Head },
  layout: DashboardLayout,
  props: {
    seasons: Array,
    forceClosedGlobal: Boolean,
  },
  data() {
    return {
      showModal: false,
      editingSeason: null,
      localForceClosedGlobal: this.forceClosedGlobal,
      seasonForm: {
        id: null,
        name: '',
        start_date: '',
        end_date: '',
        force_closed: false,
        pricing: {
          adults: 10,
          kids: 7,
          umbrella: 27,
          station: 30,
          extra_bed: 3,
        },
      },
    };
  },
  computed: {
    sortedSeasons() {
      return [...this.seasons].sort((a, b) => {
        return new Date(b.start_date) - new Date(a.start_date);
      });
    },
  },
  methods: {
    formatDate(date) {
      return moment(date).format('DD/MM/YYYY');
    },
    isActiveSeason(season) {
      const now = moment();
      const start = moment(season.start_date);
      const end = moment(season.end_date);
      return now.isBetween(start, end, 'day', '[]') && !season.force_closed && !this.localForceClosedGlobal;
    },
    openSeasonModal(season) {
      if (season) {
        this.editingSeason = season;
        this.seasonForm = { ...season, pricing: { ...season.pricing } };
      } else {
        this.editingSeason = null;
        this.seasonForm = {
          id: null,
          name: '',
          start_date: '',
          end_date: '',
          force_closed: false,
          pricing: {
            adults: 10,
            kids: 7,
            umbrella: 27,
            station: 30,
            extra_bed: 3,
          },
        };
      }
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.editingSeason = null;
    },
    saveSeason() {
      router.post('/config/seasons', this.seasonForm, {
        onSuccess: () => {
          this.closeModal();
        },
      });
    },
    deleteSeason(seasonId) {
      if (confirm('Sei sicuro di voler eliminare questa stagione?')) {
        router.delete(`/config/seasons/${seasonId}`);
      }
    },
    toggleGlobalClosure() {
      router.post('/config/force-closed', {
        force_closed_global: this.localForceClosedGlobal,
      });
    },
  },
};
</script>
